import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Transaction } from '@shared/contracts/entities';
import { getAccounts, getTransactions, type TransactionFilters } from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, ListRow, StatusBadge, TransactionMark } from '@/components/banking';
import { EmptyState, ErrorState, LoadingRegion, RowSkeleton } from '@/components/feedback';
import { Sheet } from '@/components/feedback/Sheet';
import { formatDayGroup, formatTime } from '@/services/formatters';

/**
 * Transaction feed — WF-SMEM-04.
 *
 * Mobile-appropriate filtering: a bottom sheet with a small number of decisions
 * rather than a desktop filter bar. Filter and scroll state are held in the URL
 * so returning from a detail screen restores exactly what the user was looking
 * at (package navigation rule: "Preserve user context, filters and scroll
 * position where appropriate").
 */

const PAGE_SIZE = 20;

export function TransactionsPage() {
  const [params, setParams] = useSearchParams();
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const [filterOpen, setFilterOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(params.get('q') ?? '');
  const [pages, setPages] = useState(1);
  const listRef = useRef<HTMLDivElement | null>(null);

  const accountId = params.get('accountId');
  const currency = params.get('currency');
  const direction = params.get('direction') as 'in' | 'out' | null;
  const status = params.get('status') as Transaction['status'] | null;
  const search = params.get('q');

  const filters: TransactionFilters = useMemo(
    () => ({
      ...(accountId ? { accountId } : {}),
      ...(direction ? { direction } : {}),
      ...(status ? { status } : {}),
      ...(search ? { search } : {}),
    }),
    [accountId, direction, status, search],
  );

  const accounts = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getAccounts(companyId, userId)).data;
  }, [companyId, userId]);

  const page = useAsync(async () => {
    if (!companyId || !userId) throw new Error('no session');
    return (await getTransactions(companyId, userId, filters, null, PAGE_SIZE * pages)).data;
  }, [companyId, userId, filters, pages]);

  // Restore the scroll position the user left from.
  useEffect(() => {
    const saved = sessionStorage.getItem('tx-scroll');
    if (saved && page.status === 'success') {
      window.scrollTo({ top: Number(saved), behavior: 'auto' });
      sessionStorage.removeItem('tx-scroll');
    }
  }, [page.status]);

  if (!resolved) return null;

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
    setPages(1);
  };

  const activeFilterCount = [accountId, direction, status, currency].filter(Boolean).length;

  // Currency is filtered client-side because the adapter filters by account.
  const items = (page.data?.items ?? []).filter((t) => !currency || t.currency === currency);

  // Group by day so a long feed stays scannable on a phone.
  const groups: { day: string; rows: Transaction[] }[] = [];
  for (const tx of items) {
    const day = formatDayGroup(tx.occurredAt);
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.rows.push(tx);
    else groups.push({ day, rows: [tx] });
  }

  const hasMore = (page.data?.nextCursor ?? null) !== null;

  return (
    <>
      <MobileHeader
        title="Transactions"
        backTo={accountId ? `/mobile/accounts/${accountId}` : '/mobile/more'}
        backLabel={accountId ? 'account' : 'More'}
        trailing={
          <button
            type="button"
            className="tm-btn tm-btn--quiet tm-btn--sm"
            onClick={() => setFilterOpen(true)}
            aria-label={
              activeFilterCount > 0
                ? `Filter transactions, ${activeFilterCount} filters active`
                : 'Filter transactions'
            }
          >
            Filter
            {activeFilterCount > 0 ? <span className="tm-count">{activeFilterCount}</span> : null}
          </button>
        }
      />

      <div className="tm-stack" ref={listRef}>
        <div className="tm-field">
          <label className="tm-label tm-visually-hidden" htmlFor="tx-search">
            Search transactions
          </label>
          <input
            id="tx-search"
            className="tm-input"
            type="search"
            inputMode="search"
            placeholder="Search by name or reference"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setParam('q', searchDraft.trim() || null);
            }}
            onBlur={() => setParam('q', searchDraft.trim() || null)}
          />
        </div>

        {activeFilterCount > 0 || search ? (
          <div className="tm-inline tm-wrap" role="status">
            <span className="tm-hint">Showing filtered results</span>
            <button
              type="button"
              className="tm-btn tm-btn--quiet tm-btn--sm"
              onClick={() => {
                setSearchDraft('');
                setParams(new URLSearchParams(), { replace: true });
                setPages(1);
              }}
            >
              Clear all filters
            </button>
          </div>
        ) : null}

        {page.status === 'loading' ? (
          <LoadingRegion label="Loading transactions">
            <div className="tm-card tm-card--flush">
              <RowSkeleton count={6} />
            </div>
          </LoadingRegion>
        ) : page.status === 'error' ? (
          <ErrorState error={page.error} onRetry={page.reload} />
        ) : items.length === 0 ? (
          <EmptyState
            title={activeFilterCount > 0 || search ? 'No matching transactions' : 'No transactions yet'}
            body={
              activeFilterCount > 0 || search
                ? 'Try removing a filter or searching for something else.'
                : 'Once money moves in or out of your accounts, it will appear here.'
            }
            {...(activeFilterCount > 0 || search
              ? {
                  action: (
                    <button
                      type="button"
                      className="tm-btn tm-btn--secondary"
                      onClick={() => {
                        setSearchDraft('');
                        setParams(new URLSearchParams(), { replace: true });
                      }}
                    >
                      Clear filters
                    </button>
                  ),
                }
              : {})}
          />
        ) : (
          <>
            {groups.map((group) => (
              <section key={group.day} aria-labelledby={`day-${group.day}`}>
                <h2 className="tm-day-header" id={`day-${group.day}`}>
                  {group.day}
                </h2>
                <div className="tm-card tm-card--flush">
                  <ul className="tm-rows">
                    {group.rows.map((tx) => (
                      <li key={tx.id}>
                        <ListRow
                          to={`/mobile/transactions/${tx.id}`}
                          leading={<TransactionMark direction={tx.direction} />}
                          title={tx.counterparty}
                          meta={`${formatTime(tx.occurredAt)}${tx.reference ? ` · ${tx.reference}` : ''}`}
                          end={
                            <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                              <Amount
                                minorUnits={tx.amount}
                                currency={tx.currency}
                                signed
                                direction={tx.direction}
                              />
                              {tx.status !== 'completed' ? <StatusBadge status={tx.status} /> : null}
                            </span>
                          }
                          ariaLabel={`${tx.counterparty}, ${tx.direction === 'in' ? 'money in' : 'money out'}, ${tx.status}, ${formatDayGroup(tx.occurredAt)}`}
                          state={{ from: 'transactions' }}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            ))}

            {hasMore ? (
              <button
                type="button"
                className="tm-btn tm-btn--secondary tm-btn--block"
                onClick={() => {
                  sessionStorage.setItem('tx-scroll', String(window.scrollY));
                  setPages((p) => p + 1);
                }}
              >
                Show older transactions
              </button>
            ) : (
              <p className="tm-hint" style={{ textAlign: 'center' }}>
                {/* Cursor pagination: no fabricated total page count. */}
                Showing {items.length} transaction{items.length === 1 ? '' : 's'}
              </p>
            )}
          </>
        )}
      </div>

      {/* Mobile filter sheet: few, high-value decisions. */}
      <Sheet open={filterOpen} onClose={() => setFilterOpen(false)} title="Filter transactions">
        <div className="tm-stack">
          <fieldset className="tm-fieldset">
            <legend className="tm-label">Direction</legend>
            <div className="tm-chips">
              {[
                { value: null, label: 'All' },
                { value: 'in', label: 'Money in' },
                { value: 'out', label: 'Money out' },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  className={`tm-chip${direction === option.value ? ' tm-chip--on' : ''}`}
                  aria-pressed={direction === option.value}
                  onClick={() => setParam('direction', option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="tm-fieldset">
            <legend className="tm-label">Status</legend>
            <div className="tm-chips">
              {[
                { value: null, label: 'Any' },
                { value: 'completed', label: 'Completed' },
                { value: 'pending', label: 'Pending' },
                { value: 'failed', label: 'Failed' },
                { value: 'reversed', label: 'Reversed' },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  className={`tm-chip${status === option.value ? ' tm-chip--on' : ''}`}
                  aria-pressed={status === option.value}
                  onClick={() => setParam('status', option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="tm-field">
            <label className="tm-label" htmlFor="tx-account">
              Account
            </label>
            <select
              id="tx-account"
              className="tm-select"
              value={accountId ?? ''}
              onChange={(e) => setParam('accountId', e.target.value || null)}
            >
              <option value="">All accounts</option>
              {(accounts.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.displayName} ({a.currency})
                </option>
              ))}
            </select>
          </div>

          <button type="button" className="tm-btn tm-btn--block" onClick={() => setFilterOpen(false)}>
            Show results
          </button>
        </div>
      </Sheet>
    </>
  );
}
